//* 장례식장 추가정보(이름·설명·사진) 편집 — 배열 순서가 곧 부고 노출 순서
import { Dispatch, FC, SetStateAction, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { postImage } from '@shared/api/uploadImage';
import { BucketImage } from '@shared/components/BucketImage';
import { notificateError } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';

import { AdditionalInfoDraft, nextDraftKey } from './funeralHomeAdditionalInfo.util';

interface Props {
  items: AdditionalInfoDraft[];
  setItems: Dispatch<SetStateAction<AdditionalInfoDraft[]>>;
}

//* 모노톤 · 컴팩트 — 이 섹션 안에서는 브랜드 컬러를 쓰지 않는다(회색 + 얇은 테두리)
const ghostButton =
  'rounded border px-2 py-1 text-xs theme-text-8 theme-border-1 hover:theme-bg-6 disabled:opacity-30 disabled:hover:bg-transparent';
const compactField =
  'w-full rounded border px-2 text-sm theme-bg-1 theme-text-1 theme-border-1 placeholder:theme-text-6 focus:outline-none';

export const FuneralHomeAdditionalInfoEditor: FC<Props> =
  function FuneralHomeAdditionalInfoEditor({ items, setItems }: Props) {
    const { assetRole } = useAuth();
    const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
    const [uploadingKey, setUploadingKey] = useState<string | null>(null);

    const patchItem = (key: string, patch: Partial<AdditionalInfoDraft>) =>
      setItems((prev) =>
        prev.map((item) => (item.key === key ? { ...item, ...patch } : item)),
      );

    const addItem = () =>
      setItems((prev) => [
        ...prev,
        { key: nextDraftKey(), name: '', description: '', image: undefined },
      ]);

    const removeItem = (key: string) =>
      setItems((prev) => prev.filter((item) => item.key !== key));

    //* 순서 이동 (배열 순서 = 부고 노출 순서)
    const moveItem = (index: number, diff: number) =>
      setItems((prev) => {
        const target = prev[index];
        const swap = prev[index + diff];
        if (!target || !swap) return prev;
        const next = [...prev];
        next[index] = swap;
        next[index + diff] = target;
        return next;
      });

    const onImageSelect = async (key: string, file?: File) => {
      if (!file) return;
      setUploadingKey(key);
      try {
        const uploaded = await postImage(file, assetRole());
        patchItem(key, { image: { _id: uploaded._id, url: uploaded.url } });
      } catch (error) {
        notificateError(notifiacationInstance, '사진 업로드 실패');
        console.error(error);
      } finally {
        setUploadingKey(null);
      }
    };

    return (
      <div className="space-y-2 border-t pt-4 theme-border-1">
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <h5 className="font-medium">추가정보</h5>
            <span className="text-xs theme-text-8">주차안내 아래 순서대로</span>
          </div>
          {items.length > 0 && (
            <button type="button" onClick={addItem} className={ghostButton}>
              + 항목
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <button
            type="button"
            onClick={addItem}
            className="w-full rounded border border-dashed py-3 text-xs theme-text-8 theme-border-1 hover:theme-bg-6"
          >
            + 항목 추가
          </button>
        ) : (
          <div className="space-y-2">
            {items.map((item, index) => (
              <div
                key={item.key}
                className="space-y-1.5 rounded-md border p-2.5 theme-bg-1 theme-border-1"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs theme-text-8">{index + 1}</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() => moveItem(index, -1)}
                      className={ghostButton}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      disabled={index === items.length - 1}
                      onClick={() => moveItem(index, 1)}
                      className={ghostButton}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => removeItem(item.key)}
                      className={`${ghostButton} hover:text-warn`}
                    >
                      삭제
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  autoComplete="off"
                  className={`${compactField} h-9`}
                  placeholder="제목 (예: 주차 안내)"
                  value={item.name}
                  onChange={(e) => patchItem(item.key, { name: e.target.value })}
                />
                <textarea
                  className={`${compactField} h-20 resize-none py-1.5`}
                  placeholder="설명 (선택) · 줄바꿈 그대로 노출"
                  value={item.description}
                  onChange={(e) => patchItem(item.key, { description: e.target.value })}
                />
                <div className="flex items-center gap-2">
                  {item.image && (
                    <BucketImage
                      value={item.image.url}
                      alt={item.name}
                      width={44}
                      height={44}
                      className="rounded object-cover"
                    />
                  )}
                  <label className={`${ghostButton} cursor-pointer`}>
                    {item.image ? '사진 변경' : '사진 추가'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        //* 같은 파일 다시 고를 때도 onChange가 뜨게 비워둔다
                        e.target.value = '';
                        onImageSelect(item.key, file);
                      }}
                    />
                  </label>
                  {item.image && (
                    <button
                      type="button"
                      onClick={() => patchItem(item.key, { image: undefined })}
                      className={`${ghostButton} hover:text-warn`}
                    >
                      사진 삭제
                    </button>
                  )}
                  {uploadingKey === item.key && (
                    <span className="text-xs theme-text-8">업로드 중…</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };
